import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import PlayerName, { usePlayerDecorations } from '../Common/PlayerName.tsx';
import './DailyStreak.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

type LeaderboardPeriod = 'today' | 'month' | 'legends';

interface LeaderboardData {
  top: any[];
  dateKey?: string;
  period: LeaderboardPeriod;
  title: string;
  emoji: string;
}

const DailyStreakLeaderboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<LeaderboardPeriod>('today');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    async function fetchLeaderboard() {
      if (!user) return;
      setLoadingData(true);
      
      try {
        const token = await user.getIdToken();
        const endpoint = activeTab === 'today' ? '/today' : 
                        activeTab === 'month' ? '/month' : 
                        '/legends';
        
        const res = await fetch(`${API_URL}/api/daily-streak/leaderboard${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setLeaderboardData(data);
      } catch (error) {
        console.error('Erreur lors du chargement du classement:', error);
      } finally {
        setLoadingData(false);
      }
    }
    fetchLeaderboard();
  }, [user, activeTab]);

  // Charger les effets de Noël pour tous les utilisateurs du leaderboard
  const userIds = leaderboardData?.top?.map((r: any) => r.user_id) || [];
  const { decorations } = usePlayerDecorations(userIds);

  const renderTodayLeaderboard = () => {
    return (
      <div className="leaderboard-content">
        <div className="leaderboard-subtitle">
          📅 {leaderboardData?.dateKey || 'Aujourd\'hui'}
        </div>
        {!leaderboardData?.top || leaderboardData.top.length === 0 ? (
          <div className="leaderboard-empty">
            🎭 Aucun score enregistré pour aujourd'hui.<br/>
            Soyez le premier à relever le défi !
          </div>
        ) : (
          <ol className="leaderboard-list">
            {leaderboardData.top.map((r: any, i: number) => (
              <li key={i} className={`leaderboard-item ${i < 3 ? 'top-three' : ''}`}>
                <span className="leaderboard-rank">
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                </span>
                <PlayerName 
                  userId={r.user_id}
                  userName={r.user_name || r.user_id}
                  decoration={decorations[r.user_id]}
                  className="leaderboard-name"
                />
                <span className="leaderboard-score">
                  {r.score || 0} pts • {r.duration_sec || 0}s
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  const renderMonthLeaderboard = () => {
    return (
      <div className="leaderboard-content">
        <div className="leaderboard-subtitle">
          📊 Performances mensuelles
        </div>
        {!leaderboardData?.top || leaderboardData.top.length === 0 ? (
          <div className="leaderboard-empty">
            🗓️ Aucun maître ce mois-ci.<br/>
            Dominez le classement mensuel !
          </div>
        ) : (
          <ol className="leaderboard-list">
            {leaderboardData.top.map((r: any, i: number) => (
              <li key={i} className={`leaderboard-item monthly-item ${i < 3 ? 'top-three' : ''}`}>
                <span className="leaderboard-rank">
                  {i === 0 ? '👑' : i === 1 ? '🏆' : i === 2 ? '🥉' : `#${i + 1}`}
                </span>
                <div className="leaderboard-info">
                  <div className="leaderboard-name">
                    <PlayerName 
                      userId={r.user_id}
                      userName={r.user_name || r.user_id}
                      decoration={decorations[r.user_id]}
                    />
                  </div>
                  <div className="leaderboard-stats">
                    <span className="stat-badge total-score">{r.total_score || 0} pts</span>
                    <span className="stat-badge days-played">{r.days_played || 0} jours</span>
                    <span className="stat-badge avg-score">Moy: {r.avg_score || 0}</span>
                    <span className="stat-badge best-score">Max: {r.best_score || 0}</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  const renderLegendsLeaderboard = () => {
    return (
      <div className="leaderboard-content">
        <div className="leaderboard-subtitle">
          ⚡ Statistiques éternelles (juste de l'année mais chuttttt !!!)
        </div>
        
        {/* Guide des badges d'ancienneté */}
        <div className="veterans-guide">
          <div className="guide-title">📊 Guide des Niveaux d'Ancienneté</div>
          <div className="guide-badges">
            <span className="guide-badge novice">🌱 Novice (&lt; 7j)</span>
            <span className="guide-badge regulier">🔥 Régulier (7-29j)</span>
            <span className="guide-badge fidele">⭐ Fidèle (30-89j)</span>
            <span className="guide-badge veteran">💎 Vétéran (90-364j)</span>
            <span className="guide-badge legende">👑 Légende (365j+)</span>
          </div>
        </div>
        
        {!leaderboardData?.top || leaderboardData.top.length === 0 ? (
          <div className="leaderboard-empty">
            👑 Le Hall des Légendes attend ses héros.<br/>
            Forgez votre légende !
          </div>
        ) : (
          <ol className="leaderboard-list">
            {leaderboardData.top.map((r: any, i: number) => (
              <li key={i} className={`leaderboard-item legends-item ${i < 3 ? 'top-three legend-tier' : ''}`}>
                <span className="leaderboard-rank">
                  {i === 0 ? '⚡' : i === 1 ? '🌟' : i === 2 ? '💎' : `#${i + 1}`}
                </span>
                <div className="leaderboard-info">
                  <div className="leaderboard-name">
                    <PlayerName 
                      userId={r.user_id}
                      userName={r.user_name || r.user_id}
                      decoration={decorations[r.user_id]}
                    />
                    {i < 3 && <span className="legend-badge">LÉGENDE</span>}
                  </div>
                  <div className="leaderboard-stats legends-stats">
                    <span className="stat-badge lifetime-score">💰 {(r.lifetime_score || 0).toLocaleString()} pts</span>
                    <span className="stat-badge total-days">🎮 {r.total_days || 0} jours</span>
                    <span className="stat-badge record-score">🏆 Max: {r.legendary_score || 0}</span>
                    <span className="stat-badge record-time">⏱️ Cumulé: {(() => {
                      const totalSec = r.cumulative_time || 0;
                      const hours = Math.floor(totalSec / 3600);
                      const mins = Math.floor((totalSec % 3600) / 60);
                      if (hours > 0) return `${hours}h${mins}m`;
                      if (mins > 0) return `${mins}m${totalSec % 60}s`;
                      return `${totalSec}s`;
                    })()}</span>
                    <span className="stat-badge veteran">
                      {(() => {
                        const days = r.days_since_first || 0;
                        if (days < 7) return `🌱 Novice (${days}j)`;
                        if (days < 30) return `🔥 Régulier (${days}j)`;
                        if (days < 90) return `⭐ Fidèle (${days}j)`;
                        if (days < 365) return `💎 Vétéran (${days}j)`;
                        return `👑 Légende (${days}j)`;
                      })()}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ padding: 20 }}>Chargement…</div>;
  if (!user) return null;

  return (
    <div className="daily-streak-container">
      <div className="daily-streak-header">
        <h2 className="daily-streak-title">Classements Daily Streak</h2>
        <div className="daily-streak-nav">
          <button 
            onClick={() => navigate('/daily')} 
            className="daily-streak-nav-link"
            disabled={user.isAnonymous}
            title={user.isAnonymous ? 'Créez un compte pour jouer' : ''}
          >
            {user.isAnonymous ? '🔒 Jouer' : '🎮 Jouer'}
          </button>
          <button onClick={() => navigate('/')} className="daily-streak-back-btn">
            ← Retour
          </button>
        </div>
      </div>

      {/* Onglets créatifs */}
      <div className="leaderboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
        >
          <span className="tab-emoji">🌟</span>
          <span className="tab-text">Aujourd'hui</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'month' ? 'active' : ''}`}
          onClick={() => setActiveTab('month')}
        >
          <span className="tab-emoji">🗓️</span>
          <span className="tab-text">Ce Mois</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'legends' ? 'active' : ''}`}
          onClick={() => setActiveTab('legends')}
        >
          <span className="tab-emoji">👑</span>
          <span className="tab-text">Légendes</span>
        </button>
      </div>

      <div className="daily-streak-leaderboard">
        {loadingData ? (
          <div className="leaderboard-loading">
            <div className="loading-spinner">⚡</div>
            <div>Chargement des classements...</div>
          </div>
        ) : (
          <>
            <h3 className="leaderboard-title">
              {leaderboardData?.emoji} {leaderboardData?.title}
            </h3>
            {activeTab === 'today' && renderTodayLeaderboard()}
            {activeTab === 'month' && renderMonthLeaderboard()}
            {activeTab === 'legends' && renderLegendsLeaderboard()}
          </>
        )}
      </div>
    </div>
  );
};

export default DailyStreakLeaderboard;
